import React, { useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { PriceTrendChartProps, PriceTrendSeries } from '@Pages/Detail/props';
import s from './style.module.css';

const PriceTrendChart = (props: PriceTrendChartProps) => {
    const { priceTrend, courseName } = props;
    // 価格履歴が0件の会員種別も含めて一覧化する。除外すると「準備中です」フォールバック
    // （PriceTrendSummaryGenerator#empty_summary）が表示されず、他の会員種別のグラフが
    // 無言で代わりに表示されてしまう（#2061）。
    const seriesList = priceTrend.series;

    // 価格履歴のある会員種別を優先して初期選択する。先頭の会員種別だけ0件の場合に
    // 「準備中です」から始まってしまう不具合を避ける（#2090）
    const [activeId, setActiveId] = useState<number>(
        (seriesList.find((s) => s.points.length > 0) ?? seriesList[0])?.membershipId ?? 0
    );

    // 登録会員種別が1つもない、またはどの会員種別も価格履歴が0件のコースは
    // セクション自体を描画しない（「準備中です」の空表示だけが並ぶのを防ぐ）
    const hasAnyData = seriesList.some((s) => s.points.length > 0);
    if (seriesList.length === 0 || !hasAnyData) return null;

    const activeSeries = seriesList.find((s) => s.membershipId === activeId) || seriesList[0];

    return (
        <section className={s.section} aria-label={`${courseName}の会員権相場推移`}>
            <div className={s.heading_box}>
                <h2 className={s.heading}>会員権相場推移</h2>
                <p className={s.heading_note}>※ 相場価格＝売買の件数で加重平均</p>
            </div>

            {seriesList.length >= 2 && (
                <div className={s.member_type_switch_box} role='tablist' aria-label='会員種別の切り替え'>
                    {seriesList.map((s2) => {
                        const isActive = s2.membershipId === activeSeries.membershipId;
                        return (
                            <button
                                key={s2.membershipId}
                                role='tab'
                                aria-selected={isActive}
                                onClick={() => setActiveId(s2.membershipId)}
                                className={`${s.member_type_switch} ${isActive ? s.on : s.off}`}
                            >
                                {s2.name}
                            </button>
                        );
                    })}
                </div>
            )}

            <p className={s.summary}>{activeSeries.summary}</p>

            <PriceChart series={activeSeries} courseName={courseName} />
            <PriceTrendTable series={activeSeries} />

            {/* priceTrend.updatedAt は全会員種別の最新月を横断集計した値のため、
                データが0件のactiveSeriesでは無関係な更新月を示してしまう（表示しない） */}
            {activeSeries.points.length > 0 && priceTrend.updatedAt && (
                <p className={s.source}>出典: 住地ゴルフ調べ / 更新月 {priceTrend.updatedAt}</p>
            )}
        </section>
    );
};

// X軸ラベル（"YYYY/MM"）が重ならずに収まる最大表示本数。initialDimension.width=321から
// YAxis幅56・右margin12を差し引いたプロット幅は約253px。PriceTrendQueryの対象は実質13ヶ月
// （12ヶ月前〜当月、`version >= period_start`比較のためpointsは13点になりうる）で、
// 13点をrenderToStaticMarkupで実測すると間引きなし（interval=0）ではtick間隔約21px、
// fontSize10のラベル幅実測（約38-42px）と衝突するため、6本（13点ならinterval=2、
// 間隔約64px）まで間引く
const SP_MAX_VISIBLE_X_LABELS = 6;

const PriceChart = (props: { series: PriceTrendSeries; courseName: string }) => {
    // 0件のときは summary の「準備中です」フォールバックのみで案内するため、
    // ここで「2ヶ月以上のデータが必要」を重ねて表示しない
    if (props.series.points.length === 0) return null;
    if (props.series.points.length < 2) {
        return <div className={s.single_point_fallback}>推移グラフの表示には2ヶ月以上のデータが必要です。</div>;
    }

    // interval='preserveEnd'（recharts既定）はラベル文字幅の実測（DOM依存）で間引き本数を
    // 決めるためSSR/CSR間でhydration mismatchが起こりうる。データ点数のみから決まる
    // 間引き本数を明示指定し、SSR/CSR双方で同じ結果になるようにする
    const xAxisInterval =
        props.series.points.length > SP_MAX_VISIBLE_X_LABELS
            ? Math.ceil(props.series.points.length / SP_MAX_VISIBLE_X_LABELS) - 1
            : 0;

    return (
        <div
            className={s.chart_wrap}
            role='img'
            aria-label={`${props.courseName} ${props.series.name}の月次相場推移グラフ`}
        >
            {/* initialDimension: SSR時（window/document無し）はResponsiveContainerがDOM幅を
                測定できず子要素を描画しないため、AIクローラーがグラフを読めない（東都ゴルフと
                同じ失敗）。SSR固定寸法を与えつつクライアントでは従来通りResizeObserverで
                追従させる。値はモバイル標準幅375pxから.section（margin:0 12px, padding:0 14px,
                border:1px）の左右合計54pxを差し引いた内側幅=375-24-28-2=321を見積もった */}
            <ResponsiveContainer width='100%' height={240} initialDimension={{ width: 321, height: 240 }}>
                <LineChart data={props.series.points} margin={{ top: 16, right: 12, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray='3 3' stroke='#e5e5e5' />
                    <XAxis dataKey='monthLabel' tick={{ fontSize: 10 }} interval={xAxisInterval} />
                    <YAxis
                        tickFormatter={(value) => `${Number(value).toLocaleString()}`}
                        tick={{ fontSize: 10 }}
                        width={56}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Line
                        type='monotone'
                        dataKey='marketPrice'
                        name={props.series.name}
                        stroke='#31599a'
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const ChartTooltip = (tooltipProps: TooltipProps<number, string>) => {
    const { active, payload } = tooltipProps;
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0].payload;
    return (
        <div className={s.tooltip}>
            <div className={s.tooltip_month}>{data.monthLabel}</div>
            <div className={s.tooltip_price}>{Number(data.marketPrice).toLocaleString()}万円</div>
            <div className={s.tooltip_offers}>
                売 {data.sellingOffers}件 / 買 {data.buyingOffers}件
            </div>
        </div>
    );
};

const PriceTrendTable = (props: { series: PriceTrendSeries }) => {
    if (props.series.points.length === 0) return null;

    return (
        <div className={s.table_wrap}>
            <table className={s.price_table}>
                <thead>
                    <tr>
                        <th scope='col'>月</th>
                        <th scope='col'>相場価格</th>
                        <th scope='col'>売件</th>
                        <th scope='col'>買件</th>
                    </tr>
                </thead>
                <tbody>
                    {props.series.points.map((p) => (
                        <tr key={p.month}>
                            <td>{p.monthLabel}</td>
                            <td>{p.marketPrice.toLocaleString()}万</td>
                            <td>{p.sellingOffers}</td>
                            <td>{p.buyingOffers}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PriceTrendChart;
